export function jsonToCsvFinanceBatch(jsonArray: any[], startDate: string, endDate: string, arrayDelimiter = ';'): string {
  const flattenObject = (obj: Record<string, any>, parentKey = '', res: Record<string, any> = {}): Record<string, any> => {
    for (const key in obj) {
      const propName = parentKey ? `${parentKey}.${key}` : key;
      if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        flattenObject(obj[key], propName, res);
      } else if (Array.isArray(obj[key])) {
        obj[key].forEach((item, index) => {
          flattenObject(item, `${propName}[${index}]`, res);
        });
      } else {
        res[propName] = obj[key];
      }
    }
    return res;
  };

  const flatArray = jsonArray.map((item: any) => flattenObject(item));
  const columnMapping: { [key: string]: string } = {
    'transactionDate': 'Transaction Date',
    'status': 'Status',
    'journalEntryNumber': 'Journal Entry Number',
    'glBatchNumber': 'GL Batch Number',
    'accountingType': 'Accounting Type',
    'glAccountId': 'GL Account Number',
    'amount': 'Amount',
    'createdAt': 'Created At',
    'updatedAt': 'Updated At',
    'respondedAt': 'Responded At',
  };

  const headers = Object.keys(columnMapping).map(key => columnMapping[key]);

  const reverseMapping = headers.reduce((acc, header) => {
    acc[header] = Object.keys(columnMapping).find(key => columnMapping[key] === header) || header;
    return acc;
  }, {} as Record<string, string>);

  const start = new Date(startDate);
  const end = new Date(endDate);

  const filteredArray = flatArray.filter((row: any) => {
    const transactionDate = new Date(row['transactionDate']);
    return transactionDate >= start && transactionDate <= end;
  });

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const expandedArray = filteredArray.flatMap((row: any) => {
    const accountingTypes = Array.isArray(row['accountingType']) ? row['accountingType'] : [row['accountingType']];
    const glAccountNumbers = Array.isArray(row['glAccountNumber']) ? row['glAccountNumber'] : [row['glAccountNumber']];
    const amounts = Array.isArray(row['amount']) ? row['amount'] : [row['amount']];

    const maxLength = Math.max(accountingTypes.length, glAccountNumbers.length, amounts.length);
    const expandedRows: any[] = [];

    for (let i = 0; i < maxLength; i++) {
      expandedRows.push({
        ...row,
        status: row['status'],
        accountingType: accountingTypes[i] || accountingTypes[0],
        glAccountNumber: glAccountNumbers[i] || glAccountNumbers[0],
        amount: amounts[i] || amounts[0],
        createdAt: row['createdAt'],
        updatedAt: row['updatedAt'],
        respondedAt: row['respondedAt'],
      });
    }

    return expandedRows;
  });

  const centerAlign = (value: string, width: number): string => {
    const padding = Math.max(0, width - value.length);
    const padLeft = Math.floor(padding / 2);
    const padRight = padding - padLeft;
    return ' '.repeat(padLeft) + value + ' '.repeat(padRight);
  };

  const maxColumnWidth = 20; // Adjust this value as needed

  const csvRows = expandedArray.map((row: any) => {
    return headers
      .map((header) => {
        let value = row[reverseMapping[header]];
        if (header === 'Created At' || header === 'Updated At' || header === 'Responded At') {
          value = formatDateTime(value);
        }
        if (Array.isArray(value)) {
          value = value.join(arrayDelimiter);
        }
        const escaped = ('' + value).replace(/"/g, '""');
        return `"${centerAlign(escaped, maxColumnWidth)}"`;
      })
      .join(',');
  });

  return [headers.map(header => `"${centerAlign(header, maxColumnWidth)}"`).join(','), ...csvRows].join('\n');
}
